/**
 * CreateProductModal - Modal for creating new products
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { unifiedAPI, productTypesAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const CreateProductModal = ({ open, onOpenChange, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    retail_price: '',
    product_type_id: '',
    taxable: true,  // P1: Default to taxable
  });
  const [productTypes, setProductTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Fetch product types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await productTypesAPI.getProductTypes({ is_active: true });
        setProductTypes(res.product_types || []);
      } catch (err) {
        console.error('Failed to fetch product types:', err);
      }
    };
    if (open) fetchTypes();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setFormData({
        name: '',
        sku: '',
        description: '',
        retail_price: '',
        product_type_id: '',
        taxable: true,  // P1: Reset to default
      });
    }
  }, [open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }

    setSubmitting(true);
    try {
      const result = await unifiedAPI.adminCreateProduct({
        name: formData.name,
        sku: formData.sku || null,
        description: formData.description || null,
        retail_price: formData.retail_price ? parseFloat(formData.retail_price) : 0,
        product_type_id: formData.product_type_id || null,
        is_active: true,
        taxable: formData.taxable,  // P1: Include taxable flag
      });
      toast.success('Product created successfully');
      onOpenChange(false);
      onSuccess?.(result.id);
    } catch (err) {
      toast.error(err.message || 'Failed to create product');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="create-product-modal">
        <DialogHeader>
          <DialogTitle>Create New Product</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Product Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Product name"
              data-testid="product-name-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SKU</Label>
              <Input
                value={formData.sku}
                onChange={(e) => handleChange('sku', e.target.value)}
                placeholder="SKU-001"
              />
            </div>
            <div className="space-y-2">
              <Label>Base Price</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.retail_price}
                onChange={(e) => handleChange('retail_price', e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          {productTypes.length > 0 && (
            <div className="space-y-2">
              <Label>Product Type</Label>
              <select
                value={formData.product_type_id}
                onChange={(e) => handleChange('product_type_id', e.target.value)}
                className="w-full h-10 px-3 border rounded-md bg-white"
              >
                <option value="">Select type...</option>
                {productTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Product description..."
              rows={3}
            />
          </div>

          {/* P1: Taxable Checkbox */}
          <div className="flex items-center gap-2 py-2">
            <input
              type="checkbox"
              id="taxable"
              checked={formData.taxable}
              onChange={(e) => handleChange('taxable', e.target.checked)}
              className="rounded border-gray-300"
              data-testid="product-taxable-checkbox"
            />
            <Label htmlFor="taxable" className="cursor-pointer text-sm font-medium text-gray-700">
              Taxable
            </Label>
            <span className="text-xs text-gray-500">(Sales tax applies to this product)</span>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !formData.name.trim()}>
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
              ) : (
                'Create Product'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProductModal;
